import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { digitalApiUrl, digitalAssetsUrl } from "../../../Services/Api";
import {
  getBannerID,
  postBannerNew,
  putBanner,
  uploadImageBanner,
} from "../../../Services/Banner";
import { getCountries, ICountry } from "../../../Services/Country";
import { IRestGetIBanner } from "../../../Services/Interfaces";
import { TextH2 } from "../../../styles/global.styles";
import { ImgSgv } from "../../Login/stylesLogin";

enum TypeLink {
  INTERNAL = "y",
  EXTERNAL = "n",
}

/**
 * Normaliza o que vem da API para o estado dos campos, que é sempre string.
 *
 * title, url, line e displayOrder são nullable na entidade Banner. Desde que
 * a URL virou opcional — banner do plano Connection pode não ter link — a
 * API passou a devolver `url: null` de verdade, e jogar isso direto no
 * estado quebrava de dois jeitos:
 *
 *   - `url.trim()` ao salvar estourava "can't access property trim of null";
 *   - o TextField trocava de controlado para não controlado no meio do
 *     caminho, que é o mesmo defeito em versão silenciosa.
 *
 * line e displayOrder chegam como number: viram texto aqui porque o resto
 * da tela trata os dois como string, inclusive o `disabled` do botão, que
 * chama `.length` neles.
 */
const comoTexto = (valor: unknown): string =>
  valor === null || valor === undefined ? "" : String(valor);

export const BannerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bannerId } = useParams();

  const [name, setName] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [filename, setFilename] = useState<string>(
    digitalAssetsUrl + "/assets/banner.jpg"
  );
  const [displayOrder, setDisplayOrder] = useState<string>("");
  const [line, setLine] = useState<string>("");
  const [url, setUrl] = useState("");
  const [internalUrl, setInternalUrl] = useState<TypeLink>(TypeLink.EXTERNAL);

  const [imageFile, setImageFile] = useState<File | undefined>(undefined);

  // Países da segmentação. Lista vazia = banner global (aparece para todo
  // mundo); com países, só aparece para quem o GPS colocar dentro de algum
  // deles.
  //
  // Dois estados de nome parecido e papéis diferentes: countryOptions é o
  // catálogo vindo da API, selectedCountries é a escolha do usuário.
  const [countryOptions, setCountryOptions] = useState<ICountry[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  /** Nome para o chip; cai no id se o catálogo ainda não carregou. */
  const nomeDoPais = (id: string) =>
    countryOptions.find((c) => c.id === id)?.name ?? id;

  const isEditing = bannerId !== "New";

  const Rota = (path: string) => {
    return navigate(`${path}`, { replace: true });
  };

  const criarBanner = () => {
    const bannerData =
      url.trim().length <= 0
        ? {
            name,
            title,
            displayOrder,
            line,
            countries: selectedCountries,
          }
        : {
            name,
            title,
            displayOrder,
            line,
            url,
            internalUrl,
            countries: selectedCountries,
          };

    if (imageFile) {
      postBannerNew(bannerData).then((res) => {
        const { data } = res as IRestGetIBanner;
        const formData = new FormData();
        formData.append("upload[uploadFile][file]", imageFile);
        uploadImageBanner(data.data.id, formData).then(() => {
          Rota(`/banner`);
        });
      });
    } else {
      alert("Selecione uma imagem");
    }
  };

  const EditarBanner = () => {
    if (bannerId !== "New" && bannerId !== undefined) {
      // countries vai sempre — inclusive como lista vazia, que é como o
      // back entende "remover a segmentação e voltar a ser global"
      const bannerData =
        url.trim().length <= 0
          ? {
              displayOrder,
              isActive: isActive ? "active" : "inactive",
              countries: selectedCountries,
            }
          : {
              displayOrder,
              isActive: isActive ? "active" : "inactive",
              url,
              internalUrl,
              countries: selectedCountries,
            };

      putBanner(bannerId, bannerData).then(() => {
        if (imageFile) {
          const formData = new FormData();
          formData.append("upload[uploadFile][file]", imageFile);
          uploadImageBanner(bannerId, formData).then(() => {
            Rota(`/banner`);
          });
        } else {
          Rota(`/banner`);
        }
      });
    }
  };

  useEffect(() => {
    if (
      bannerId &&
      bannerId !== "New" &&
      location.pathname.split("/")[1] === "banner"
    ) {
      getBannerID(bannerId).then((response) => {
        const { data } = response as IRestGetIBanner;
        setLine(comoTexto(data.data.line));
        setDisplayOrder(comoTexto(data.data.displayOrder));
        setFilename(
          data.data.filename
            ? digitalApiUrl + data.data.filename
            : digitalAssetsUrl + "/assets/banner.jpg"
        );
        setIsActive(data.data.isActive);
        setTitle(comoTexto(data.data.title));
        setName(comoTexto(data.data.name));
        setUrl(comoTexto(data.data.url));
        setInternalUrl((data.data.internalUrl ? "y" : "n") as TypeLink);
        setSelectedCountries(
          (data.data.countries ?? []).map((c) => c.id)
        );
      });
    }
  }, [location.pathname, bannerId]);

  useEffect(() => {
    getCountries()
      .then(setCountryOptions)
      .catch(() => setCountryOptions([]));
  }, []);

  useEffect(() => {
    internalUrl === "n" && setUrl("");
  }, [internalUrl]);

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
    }
  };

  return (
    <Stack spacing={2}>
      <TextH2>{isEditing ? "Editar Banner" : "Novo Banner"}</TextH2>

      <Grid container spacing={2} width={"1000px"}>
        <Grid xs={6} item paddingLeft={"0px !important"}>
          <TextField
            style={{
              width: "100%",
              paddingRight: "20px",
              paddingBottom: "20px",
            }}
            autoComplete="off"
            id="name"
            label="Nome"
            variant="outlined"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>
        <Grid xs={6} item paddingLeft={"0px !important"}>
          <TextField
            style={{
              width: "100%",
              paddingBottom: "20px",
            }}
            autoComplete="off"
            id="displayOrder"
            label="Ordem de exibição"
            variant="outlined"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
          />
        </Grid>
        <Grid xs={6} item paddingLeft={"0px !important"}>
          <TextField
            style={{
              width: "100%",
              paddingRight: "20px",
              paddingBottom: "20px",
            }}
            autoComplete="off"
            id="title"
            label="Titulo (Opcional)"
            variant="outlined"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Grid>
        <Grid xs={6} item paddingLeft={"0px !important"}>
          <TextField
            style={{
              width: "100%",
              paddingBottom: "20px",
            }}
            autoComplete="off"
            id="line"
            label="Linha"
            variant="outlined"
            type="number"
            value={line}
            onChange={(e) => setLine(e.target.value)}
          />
        </Grid>
        <Grid xs={12} item paddingLeft={"0px !important"} paddingBottom={2}>
          <FormControl fullWidth>
            <InputLabel id="banner-countries-label">
              Países (Opcional)
            </InputLabel>
            <Select
              labelId="banner-countries-label"
              id="banner-countries"
              multiple
              value={selectedCountries}
              label="Países (Opcional)"
              onChange={(e) => {
                // no modo multiple o MUI entrega string[]; a string só
                // aparece no autofill do navegador, daí o split
                const valor = e.target.value;
                setSelectedCountries(
                  typeof valor === "string" ? valor.split(",") : valor
                );
              }}
              renderValue={(selecionados) =>
                (selecionados as string[]).length === 0 ? (
                  // "nenhum selecionado" é o estado global, e precisa estar
                  // escrito: um campo em branco pareceria falta de
                  // preenchimento
                  <em style={{ color: "#6b7280" }}>Todos os países</em>
                ) : (
                  <span
                    style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                  >
                    {(selecionados as string[]).map((id) => (
                      <Chip key={id} size="small" label={nomeDoPais(id)} />
                    ))}
                  </span>
                )
              }
            >
              {countryOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Checkbox checked={selectedCountries.includes(c.id)} />
                  <ListItemText primary={c.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span
            style={{
              display: "block",
              marginTop: 6,
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            Sem nenhum país escolhido, o banner aparece para todos os
            usuários. Com um ou mais países, aparece só para quem estiver em
            algum deles — o app manda a localização e o filtro é aplicado na
            listagem.
          </span>
        </Grid>
        <Grid xs={4} item paddingLeft={"0px !important"}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Tipo do link</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={internalUrl}
              label="Tipo da busca"
              defaultChecked
              onChange={(e) => setInternalUrl(e.target.value as TypeLink)}
            >
              <MenuItem value={TypeLink.INTERNAL}>Link interno</MenuItem>
              <MenuItem value={TypeLink.EXTERNAL}>Link externo</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {internalUrl === TypeLink.INTERNAL && (
          <Grid xs={8} item paddingBottom={1}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Página interna
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={url}
                label="Página interna"
                defaultChecked
                onChange={(e) => setUrl(e.target.value)}
              >
                <MenuItem value={"MAPS"}>Mapas</MenuItem>
                <MenuItem value={"Networking"}>Networkings</MenuItem>
                <MenuItem value={"Explore"}>Explorar</MenuItem>
                <MenuItem value={"Messages"}>Mensagens</MenuItem>
                <MenuItem value={"About"}>Sobre</MenuItem>
                <MenuItem value={"Account"}>Minha Conta</MenuItem>
                <MenuItem value={"Support"}>Suporte</MenuItem>
                <MenuItem value={"Policy"}>Política de privacidade</MenuItem>
                <MenuItem value={"Events"}>Eventos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        {internalUrl === TypeLink.EXTERNAL && (
          <Grid xs={8} item paddingBottom={1}>
            <TextField
              style={{
                width: "100%",
                paddingRight: "20px",
              }}
              autoComplete="off"
              id="url"
              label="URL"
              variant="outlined"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </Grid>
        )}
        <Grid xs={6} item paddingLeft={"0px !important"}>
          <FormControlLabel
            control={
              <Switch
                disabled
                style={{
                  width: "100%",
                  paddingRight: "20px",
                  paddingBottom: "20px",
                }}
                checked={isActive}
                onChange={(e) => {
                  setIsActive(e.target.checked);
                }}
              />
            }
            label="Ativar Banner"
          />
          <img
            width={420}
            height={150}
            src={imageFile ? URL.createObjectURL(imageFile) : filename}
            alt="Banner"
          />
        </Grid>
        <Grid xs={6} item paddingLeft={"0px !important"}>
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
          >
            {imageFile ? imageFile.name : "Enviar Banner 420x150 (JPG, PNG)"}
            <VisuallyHiddenInput type="file" onChange={handleFileChange} />
          </Button>
          <button
            onClick={() => {
              isEditing ? EditarBanner() : criarBanner();
            }}
            disabled={
              line.length === 0 ||
              displayOrder.length === 0 ||
              name.length === 0
            }
            style={{
              backgroundColor:
                line.length === 0 ||
                displayOrder.length === 0 ||
                name.length === 0
                  ? "#86cb1757"
                  : "#1dcb17",
              border: "none",
              cursor: "pointer",
              color: "#FFFFFF",
              fontWeight: "bold",
              height: "56px",
              width: "100px",
              borderRadius: "10px",
              alignContent: "center",
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <ImgSgv
              style={{ marginRight: "5px" }}
              width={18}
              src="/icons/menu/new.svg"
            />
            {isEditing ? "Salvar" : "Criar"}
          </button>
        </Grid>
      </Grid>
    </Stack>
  );
};